import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit, MapPin, Phone, Mail, Globe, Instagram } from 'lucide-react';
import { torrefacoesService } from '../../../services/torrefacoes';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';

const TorrefacaoViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Buscar torrefação
  const { data: torrefacao, isLoading, error } = useQuery({
    queryKey: ['torrefacao', id],
    queryFn: () => torrefacoesService.getTorrefacaoById(id!),
    enabled: !!id,
  });

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'Active':
        return { label: 'Ativa', variant: 'success' };
      case 'Inactive':
        return { label: 'Inativa', variant: 'error' };
      case 'Pending':
        return { label: 'Pendente', variant: 'warning' };
      default:
        return { label: status, variant: 'default' };
    }
  };

  const getStatusBadge = (status: string) => {
    const statusInfo = getStatusInfo(status);
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
        statusInfo.variant === 'success' 
          ? 'bg-green-100 text-green-800' 
          : statusInfo.variant === 'error'
          ? 'bg-red-100 text-red-800'
          : statusInfo.variant === 'warning'
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-gray-100 text-gray-800'
      }`}>
        {statusInfo.label}
      </span>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !torrefacao) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Torrefação não encontrada</h1>
          <Link to="/torrefacoes" className="text-blue-600 hover:text-blue-800">
            Voltar para Torrefações
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/torrefacoes" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Torrefações
        </Link>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{torrefacao.name}</h1>
            <p className="text-gray-600 mt-1">{torrefacao.description}</p>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/torrefacoes/${torrefacao.id}/edit`}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações Principais */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status e Tipo */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>
            <div className="flex items-center space-x-4">
              {getStatusBadge(torrefacao.status)}
              <span className="text-sm text-gray-600">
                Tipo: <span className="font-medium">Torrefação</span>
              </span>
            </div>
          </div>

          {/* Localização */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Localização
            </h2>
            <div className="space-y-2">
              <p className="text-gray-900">
                <span className="font-medium">Cidade:</span> {torrefacao.cityName}, {torrefacao.stateName}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Endereço:</span> {torrefacao.address}
              </p>
            </div>
          </div>

          {/* Contato */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contato</h2>
            <div className="space-y-3">
              {torrefacao.phone && (
                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-gray-400 mr-3" />
                  <span className="text-gray-900">{torrefacao.phone}</span>
                </div>
              )}
              {torrefacao.email && (
                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-gray-400 mr-3" />
                  <a href={`mailto:${torrefacao.email}`} className="text-blue-600 hover:text-blue-800">
                    {torrefacao.email}
                  </a>
                </div>
              )}
              {torrefacao.website && (
                <div className="flex items-center">
                  <Globe className="h-4 w-4 text-gray-400 mr-3" />
                  <a href={torrefacao.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                    {torrefacao.website}
                  </a>
                </div>
              )}
              {torrefacao.instagram && (
                <div className="flex items-center">
                  <Instagram className="h-4 w-4 text-gray-400 mr-3" />
                  <a href={`https://instagram.com/${torrefacao.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                    {torrefacao.instagram}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Informações Empresariais */}
          {torrefacao.cnpj && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Empresariais</h2>
              <div className="space-y-2">
                <p className="text-gray-900">
                  <span className="font-medium">CNPJ:</span> {torrefacao.cnpj}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Logo */}
          {torrefacao.logoUrl && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Logo</h3>
              <img
                src={torrefacao.logoUrl}
                alt={`Logo da ${torrefacao.name}`}
                className="w-full h-32 object-contain rounded-lg"
              />
            </div>
          )}

          {/* Imagem de Capa */}
          {torrefacao.coverImageUrl && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Imagem de Capa</h3>
              <img
                src={torrefacao.coverImageUrl}
                alt={`Capa da ${torrefacao.name}`}
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          )}

          {/* Informações do Sistema */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Sistema</h3>
            <div className="space-y-2 text-sm">
              <p className="text-gray-600">
                <span className="font-medium">ID:</span> {torrefacao.id}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Criado em:</span> {new Date(torrefacao.createdAt).toLocaleDateString('pt-BR')}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Atualizado em:</span> {new Date(torrefacao.updatedAt).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TorrefacaoViewPage;